import React from "react";
import classNames from "classnames";

import { DeadSymbolByCode } from "@components/board/board";
import KeyboardButton from "@components/keyboard-button/keyboard-button";

import {
  DeadKeyElements,
  Keyboard,
  KeyboardModifications,
  SpecialKeyNames,
} from "@typings/global.types";

type PropsType = {
  rowIndex: number;
  symbols: string;
  activeSymbol: string;
  errorSymbol: string | undefined;
  keyboard: Keyboard;
  keyboardType: KeyboardModifications;
  deadSymbol: DeadSymbolByCode | undefined;
};

function RowKeyboard({
  rowIndex,
  symbols,
  activeSymbol,
  errorSymbol,
  keyboard,
  keyboardType,
  deadSymbol,
}: PropsType) {
  // перевіряємо на наявність кнопки в обєкті deadKeyElements
  const searchDeadKey = (
    deadKeyElem: DeadKeyElements | undefined,
    nextKey: string,
  ): string => {
    let res = "";

    if (deadKeyElem) {
      Object.entries(deadKeyElem).forEach((item) => {
        if (item[1].indexOf(nextKey) !== -1) {
          // eslint-disable-next-line
          res = item[0];
        }
      });
    }

    return res;
  };

  // перевіряємо на наявність кнопки в обєкті deadKeyElements
  // перевіряємо на наявність кнопки в масиві активної клавіатури
  const isDKeyArr = (arr: string[] | undefined): boolean => {
    let res = false;

    if (arr) {
      Object.values(arr).forEach((key) => {
        const arrSplit = key.split(" ");
        if (deadSymbol && arrSplit.indexOf(deadSymbol) !== -1) {
          res = true;
        }
      });
    }

    return res;
  };

  const activeKey = (key: string, nextKey: string): boolean => {
    // підсвічуємо просту букву
    if (key === nextKey) {
      return true;
    }
    // підсвічуємо "shift"
    if (keyboard.shiftElements) {
      if (
        key === "{shift}" &&
        nextKey !== " " &&
        keyboard.shiftElements.indexOf(nextKey) !== -1
      ) {
        return true;
      }
      // підсвічуємо "diacritics-shift"
      if (
        key === "{shift}" &&
        keyboard.deadKey &&
        keyboard.deadKey.indexOf(
          searchDeadKey(keyboard.deadKeyElements, nextKey),
        ) !== -1 &&
        keyboard.acceptedWithDeadKey &&
        keyboard.acceptedWithDeadKey.indexOf(nextKey) !== -1 &&
        keyboard.shiftElements.indexOf(
          searchDeadKey(keyboard.deadKeyElements, nextKey),
        ) !== -1 &&
        !deadSymbol
      ) {
        return true;
      }
      // дослідити чи треба це, бо з цим кодом підсвічуються шифти, коли треба ставити дед клавішу
      // if (
      //   key === "{shift}" &&
      //   keyboard.acceptedWithDeadKey &&
      //   keyboard.acceptedWithDeadKey.indexOf(nextKey) !== -1
      // ) {
      //   if (!isDKeyArr(keyboard[keyboardType])) {
      //     if (
      //       !deadSymbol &&
      //       !(keyboard.shiftElements.indexOf(nextKey) !== -1)
      //     ) {
      //       return true;
      //     }
      //   }
      // }
    }
    // підсвічуємо deadKey якщо вони присутні в мові
    if (
      keyboard.deadKey &&
      searchDeadKey(keyboard.deadKeyElements, nextKey) === key &&
      !deadSymbol
    ) {
      return true;
    }
    // підсвічуємо "shift" для введення великої літери після dead key
    if (
      deadSymbol &&
      nextKey.toUpperCase() === nextKey &&
      key === "{shift}" &&
      nextKey !== " "
    ) {
      return true;
    }
    // підсвічуємо "alt"
    if (keyboard.altElements) {
      if (key === "{alt}" && keyboard.altElements.indexOf(nextKey) !== -1) {
        return true;
      }
    }
    // підсвічуємо "alt-shift"
    if (keyboard.altElements && keyboard.shiftAltElements) {
      if (
        key === "{alt}" &&
        keyboard.shiftAltElements.indexOf(nextKey) !== -1
      ) {
        return true;
      }
      if (
        key === "{shift}" &&
        keyboard.shiftAltElements.indexOf(nextKey) !== -1
      ) {
        return true;
      }
    }

    return key === "{space}" && nextKey === " ";
  };

  const getFormattedSymbol = (symbol: string): string => {
    const match = symbol.match(/\{(\S+)}/);

    if (match) {
      const matchContent = match[1] as keyof SpecialKeyNames;
      return keyboard.specialKeyNames[matchContent] ?? matchContent;
    }

    return symbol;
  };

  // ширина клавіші
  const widthBtn = (btn: string): string => {
    if (/^\{\S+\}$/.test(btn)) {
      const specialFonts = "!text-extra-small-special uppercase";
      const str = "px-[0.55rem]";
      const btnKey = btn.match(/^\{(\S+)\}$/)?.[1];
      if (btnKey === "shift")
        return `w-[95px] px-2 !justify-start last:!justify-end ${specialFonts}`;
      if (btnKey === "enter")
        return `w-[74px] ${str} pr-2 !justify-end ${specialFonts}`;
      if (btnKey === "lock")
        return `w-[74px] ${str} pl-2 !justify-start ${specialFonts}`;
      if (btnKey === "tab")
        return `w-[60px] pl-2 !justify-start ${specialFonts}`;
      if (btnKey === "bksp")
        return `w-[60px] pr-2 !justify-end ${specialFonts}`;
      if (btnKey === "alt") return `w-7 ${specialFonts}`;
      if (btnKey === "space") return "w-[220px] !mr-[46px]";
    }
    return "w-7";
  };

  // колір клавіші
  const getColor = (color: string): string => {
    if (color === "c1") return "key-indigo";
    if (color === "c2") return "key-green";
    if (color === "c3") return "key-pink";
    if (color === "c4") return "key-mint";
    if (color === "c5") return "key-yellow";
    if (color === "c6") return "key-orange";
    return "key-grey";
  };

  // колір активної клавіші
  const getActiveColor = (color: string): string => {
    if (color === "c1")
      return "before:shadow-indigo-dark dark:before:shadow-indigo-dark-light";
    if (color === "c2")
      return "before:shadow-green-dark dark:before:shadow-green-dark-light";
    if (color === "c3")
      return "before:shadow-pink-dark dark:before:shadow-pink-dark-light";
    if (color === "c4")
      return "before:shadow-mint-dark dark:before:shadow-mint-dark-light";
    if (color === "c5")
      return "before:shadow-yellow-dark dark:before:shadow-yellow-dark-light";
    if (color === "c6")
      return "before:shadow-orange-dark dark:before:shadow-orange-dark-light";
    return "before:shadow-grey-dark dark:before:shadow-grey-dark-default";
  };

  return (
    <div className='flex justify-center'>
      {symbols.split(" ").map((symbol, index) => {
        return (
          <KeyboardButton
            key={`${symbol}-${index}`}
            active={activeKey(symbol, activeSymbol)}
            error={errorSymbol === getFormattedSymbol(symbol)}
            className={classNames(
              getColor(keyboard.colors[rowIndex].split(" ")[index]),
              [widthBtn(symbol)],
            )}
            activeClassName={classNames(
              getActiveColor(keyboard.colors[rowIndex].split(" ")[index]),
            )}
          >
            {getFormattedSymbol(symbol)}
          </KeyboardButton>
        );
      })}
    </div>
  );
}

export default RowKeyboard;
